import React from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  FormControlLabel,
  FormHelperText,
  Switch,
  Typography,
} from '@mui/material'
import { Grupo, GrupoCreate } from '../../types/api'

// Schema de validação será criado dinamicamente com traduções
const createGrupoSchema = (t: (key: string) => string) => z.object({
  nome: z.string().min(1, t('groups.name') + ' é obrigatório').max(80, t('groups.name') + ' deve ter no máximo 80 caracteres'),
  descricao: z.string().max(500, t('groups.description') + ' deve ter no máximo 500 caracteres').optional().nullable(),
  uuid: z.string().max(64, t('groups.uuid') + ' deve ter no máximo 64 caracteres').optional().nullable(),
  enforce_limite_acessos: z.boolean().default(true),
  limite_sessoes_por_usuario: z.preprocess(
    (value) => {
      if (value === null || value === undefined || value === '') {
        return undefined
      }
      if (typeof value === 'string') {
        const parsed = Number.parseInt(value, 10)
        return Number.isNaN(parsed) ? undefined : parsed
      }
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value
      }
      return undefined
    },
    z.number().int().min(0, t('groups.limitPerUserPositive')).optional()
  ).optional(),
  permissivo_limite_acessos: z.boolean().default(false),
})

type GrupoFormData = z.infer<ReturnType<typeof createGrupoSchema>>

interface GrupoFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: GrupoCreate) => Promise<void>
  grupo?: Grupo | null
  loading?: boolean
}

const GrupoForm: React.FC<GrupoFormProps> = ({
  open,
  onClose,
  onSubmit,
  grupo,
  loading = false,
}) => {
  const { t } = useTranslation()
  const grupoSchema = React.useMemo(() => createGrupoSchema(t), [t])
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
  } = useForm<GrupoFormData>({
    resolver: zodResolver(grupoSchema),
    defaultValues: {
      nome: grupo?.nome || '',
      descricao: grupo?.descricao || '',
      uuid: grupo?.uuid || '',
      enforce_limite_acessos: grupo?.enforce_limite_acessos ?? true,
      limite_sessoes_por_usuario: grupo?.limite_sessoes_por_usuario ?? undefined,
      permissivo_limite_acessos: grupo?.permissivo_limite_acessos ?? false,
    },
  })

  const enforceLimite = watch('enforce_limite_acessos', true)

  // Resetar formulário quando grupo mudar ou dialog abrir/fechar
  React.useEffect(() => {
    if (open) {
      reset({
        nome: grupo?.nome || '',
        descricao: grupo?.descricao || '',
        uuid: grupo?.uuid || '',
        enforce_limite_acessos: grupo?.enforce_limite_acessos ?? true,
        limite_sessoes_por_usuario: grupo?.limite_sessoes_por_usuario ?? undefined,
        permissivo_limite_acessos: grupo?.permissivo_limite_acessos ?? false,
      })
    }
  }, [open, grupo, reset])

  const handleFormSubmit = async (data: GrupoFormData) => {
    try {
      const submitData: GrupoCreate = {
        nome: data.nome,
        descricao: data.descricao || undefined,
        uuid: data.uuid?.trim() ? data.uuid.trim() : undefined,
        enforce_limite_acessos: data.enforce_limite_acessos,
        limite_sessoes_por_usuario:
          typeof data.limite_sessoes_por_usuario === 'number'
            ? data.limite_sessoes_por_usuario
            : undefined,
        permissivo_limite_acessos: data.permissivo_limite_acessos,
      }
      await onSubmit(submitData)
      reset()
      onClose()
    } catch (error) {
      // Erro será tratado pelo componente pai
      // Erro já é tratado pelo componente pai via onError
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleGenerateUuid = () => {
    const newUuid =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Math.random().toString(36).slice(2, 10)}${Math.random()
            .toString(36)
            .slice(2, 10)}`
    setValue('uuid', newUuid, { shouldDirty: true, shouldValidate: true })
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>
          {grupo ? t('groups.edit') : t('groups.new')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              {...register('nome')}
              label={t('groups.name')}
              fullWidth
              margin="normal"
              error={!!errors.nome}
              helperText={errors.nome?.message}
              disabled={loading}
              autoFocus
            />
            <TextField
              {...register('descricao')}
              label={t('groups.description')}
              fullWidth
              margin="normal"
              multiline
              rows={4}
              error={!!errors.descricao}
              helperText={errors.descricao?.message}
              disabled={loading}
            />
            <Box sx={{ mt: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'flex-end' }}
              >
                <Controller
                  name="uuid"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('groups.uuid')}
                      fullWidth
                      margin="normal"
                      error={!!errors.uuid}
                      helperText={errors.uuid?.message}
                      disabled={loading}
                      value={field.value ?? ''}
                      onChange={(event) => field.onChange(event.target.value)}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  onClick={handleGenerateUuid}
                  disabled={loading}
                  sx={{ whiteSpace: 'nowrap', minWidth: { sm: 160 } }}
                >
                  {t('groups.generateUuid')}
                </Button>
              </Stack>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Controller
                name="enforce_limite_acessos"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={Boolean(field.value)}
                        onChange={(_, checked) => field.onChange(checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1">
                          {t('groups.enforceLimit')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('groups.enforceLimitHint')}
                        </Typography>
                      </Box>
                    }
                  />
                )}
              />
            </Box>

            <TextField
              {...register('limite_sessoes_por_usuario', {
                setValueAs: (value) => {
                  if (value === '' || value === null || value === undefined) {
                    return undefined
                  }
                  const parsed = Number(value)
                  return Number.isNaN(parsed) ? undefined : parsed
                },
              })}
              label={t('groups.limitPerUser')}
              type="number"
              fullWidth
              margin="normal"
              inputProps={{ min: 0 }}
              disabled={loading || !enforceLimite}
              error={!!errors.limite_sessoes_por_usuario}
              helperText={
                errors.limite_sessoes_por_usuario?.message ||
                t('groups.limitPerUserHint')
              }
            />

            <Controller
              name="permissivo_limite_acessos"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={Boolean(field.value)}
                      onChange={(_, checked) => field.onChange(checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">
                        {t('groups.permissiveLimit')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('groups.permissiveLimitHint')}
                      </Typography>
                    </Box>
                  }
                />
              )}
            />
            {errors.permissivo_limite_acessos && (
              <FormHelperText error>
                {errors.permissivo_limite_acessos.message}
              </FormHelperText>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? t('common.loading') : t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default GrupoForm

